import React, { useMemo, useState } from "react";
import { Listbox } from "@headlessui/react";
import {
  FiCheck,
  FiChevronDown,
  FiCreditCard,
  FiHash,
  FiRefreshCw,
  FiSend,
  FiUser,
} from "react-icons/fi";
import transactionService from "../../../api/services/transactionService";

const bankTypeArr = [
  { value: "abank", name: "A Bank" },
  { value: "ayabank", name: "AYA Bank" },
  { value: "ayapay", name: "AYA Pay" },
  { value: "cbbank", name: "CB Bank" },
  { value: "cbpay", name: "CB Pay" },
  { value: "kbzbank", name: "KBZ Bank" },
  { value: "kpay", name: "K Pay" },
  { value: "mabbank", name: "MAB Bank" },
  { value: "ok", name: "OK$" },
  { value: "uabbank", name: "UAB Bank" },
  { value: "wavepay", name: "Wave Pay" },
  { value: "yomabank", name: "Yoma Bank" },
];

const Withdrawal = ({
  t,
  accountList,
  fetchAccountList,
  userDetails,
  setIsLoading,
  setIsModalOpen,
  setMessage,
  handleSongClick,
  balance,
}) => {
  const [accountNo, setAccountNo] = useState("");
  const [accountName, setAccountName] = useState("");
  const [amount, setAmount] = useState("");
  const [errorText, setErrorText] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const availableBanks = useMemo(
    () => bankTypeArr.filter((bank) => accountList.some((account) => account.bankType === bank.value)),
    [accountList]
  );

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await fetchAccountList();
    } finally {
      window.setTimeout(() => setRefreshing(false), 500);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    handleSongClick();

    if (!selectedOption) {
      setErrorText("Please select an account type.");
      return;
    }
    if (!amount || Number(amount) <= 0) {
      setErrorText("Please enter a valid amount.");
      return;
    }
    if (Number(amount) > Number(balance)) {
      setErrorText("The withdrawal amount is greater than your available balance.");
      return;
    }

    try {
      setErrorText("");
      setIsLoading(true);
      const response = await transactionService.createWithdrawal({
        userName: userDetails.username,
        userId: Number(userDetails.userId),
        bankType: selectedOption.value,
        remark: "",
        amount: Number(amount),
        accountName,
        isAdmin: false,
        accountNo,
      });

      setMessage(response.message ? t("transactionPending") : t("processSuccessful"));
      setAccountNo("");
      setAccountName("");
      setAmount("");
      setSelectedOption(null);
      setIsModalOpen(true);
      await fetchAccountList();
    } catch (error) {
      setMessage(t("sendingFail"));
      setIsModalOpen(true);
      console.error("API request error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="overflow-visible rounded-lg border border-white/15 bg-header_green/45 shadow-2xl shadow-black/25 backdrop-blur-xl">
      <div className="border-b border-white/10 px-4 py-3.5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-amber-300/15 text-amber-200">
              <FiCreditCard className="h-5 w-5" />
            </div>
            <h2 className="text-base font-semibold leading-[1.8]">{t("withdraw")}</h2>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            aria-label="Refresh balance"
            className="flex h-9 w-9 flex-none items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white/65 transition hover:bg-white/10 hover:text-white"
          >
            <FiRefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>

        <div className="mt-3 flex items-end justify-between rounded-lg border border-white/10 bg-black/15 px-4 py-2.5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/45">{t("balance")}</p>
            <p className="mt-1 text-xl tracking-wide">{Number(balance || 0).toLocaleString()}</p>
          </div>
          <span className="pb-1 text-xs text-emerald-200/70">{t("ks")}</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3.5 p-4">
        <BankSelect
          t={t}
          banks={availableBanks}
          selectedOption={selectedOption}
          setSelectedOption={(option) => {
            setSelectedOption(option);
            setErrorText("");
          }}
        />
        <Field
          id="accountNo"
          label={t("accountNo")}
          value={accountNo}
          onChange={(event) => setAccountNo(event.target.value)}
          icon={FiHash}
          inputMode="numeric"
          placeholder="—"
        />
        <Field
          id="accountName"
          label={t("accountName")}
          value={accountName}
          onChange={(event) => setAccountName(event.target.value)}
          icon={FiUser}
          placeholder="—"
        />
        <Field
          id="withdrawAmount"
          label={t("amount")}
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          icon={FiCreditCard}
          inputMode="decimal"
          type="number"
          min="1"
          max={balance || undefined}
          placeholder="0"
        />

        {errorText && (
          <div role="alert" className="rounded-lg border border-red-300/20 bg-red-400/10 px-3 py-2.5 text-xs leading-[1.7] text-red-100">
            {errorText}
          </div>
        )}

        <button
          type="submit"
          className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold leading-[1.8] text-header_green shadow-lg transition hover:bg-emerald-50 active:scale-[0.98]"
        >
          <FiSend className="h-4 w-4" />
          {t("send")}
        </button>
      </form>
    </section>
  );
};

const Field = ({ id, label, icon: Icon, ...inputProps }) => (
  <label htmlFor={id} className="block">
    <span className="mb-1.5 block text-xs font-medium leading-[1.8] text-white/75">{label}</span>
    <div className="relative">
      <Icon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
      <input
        id={id}
        required
        className="w-full rounded-lg border border-white/15 bg-header_green/55 py-3 pl-10 pr-4 text-sm leading-[1.7] text-white outline-none transition placeholder:text-white/30 focus:border-emerald-300/60 focus:bg-header_green/70 focus:ring-2 focus:ring-emerald-300/10"
        {...inputProps}
      />
    </div>
  </label>
);

const BankSelect = ({ t, banks, selectedOption, setSelectedOption }) => (
  <div>
    <span className="mb-1.5 block text-xs font-medium leading-[1.8] text-white/75">{t("selectAccountType")}</span>
    <Listbox value={selectedOption} onChange={setSelectedOption}>
      <div className="relative">
        <Listbox.Button className="flex min-h-[58px] w-full items-center rounded-lg border border-white/15 bg-header_green/55 px-3.5 py-2.5 pr-10 text-left outline-none transition focus:border-emerald-300/60 focus:ring-2 focus:ring-emerald-300/10">
          {selectedOption ? (
            <BankDetails bank={selectedOption} />
          ) : (
            <span className="text-sm leading-[1.8] text-white/45">{t("selectAccountType")}</span>
          )}
          <FiChevronDown className="absolute right-4 h-4 w-4 text-white/45" />
        </Listbox.Button>

        <Listbox.Options className="absolute z-50 mt-2 max-h-64 w-full overflow-auto rounded-lg border border-white/10 bg-header_green p-1.5 shadow-2xl outline-none">
          {banks.length ? (
            banks.map((bank) => (
              <Listbox.Option key={bank.value} value={bank}>
                {({ active, selected }) => (
                  <div className={`relative cursor-pointer rounded-lg px-3 py-2.5 pr-9 transition ${active ? "bg-white/10" : ""}`}>
                    <BankDetails bank={bank} />
                    {selected && <FiCheck className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-300" />}
                  </div>
                )}
              </Listbox.Option>
            ))
          ) : (
            <p className="px-3 py-4 text-center text-xs font-sans text-white/50">No account types available</p>
          )}
        </Listbox.Options>
      </div>
    </Listbox>
  </div>
);

const BankDetails = ({ bank }) => (
  <div className="flex items-center gap-3">
    <div className="flex h-9 w-9 flex-none items-center justify-center overflow-hidden rounded-lg bg-white p-1.5">
      <img src={`/logo/${bank.value}.png`} alt="" className="h-full w-full object-contain" />
    </div>
    <span className="text-sm font-sans font-medium text-white">{bank.name}</span>
  </div>
);

export default Withdrawal;
